import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Stack, Grid, Card, CardContent, LinearProgress, Alert, Chip, Avatar, IconButton, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
} from '@mui/material';
import { ArrowBack, LocalShipping, Payments } from '@mui/icons-material';
import { financeWorkspaceService } from '../../services/financeWorkspaceService';
import { formatIndianNumber } from '../../utils/currency';
import BusinessJourney, { JourneyStage } from '../../components/common/BusinessJourney';

const Stat: React.FC<{ label: string; value: React.ReactNode; color?: string; onClick?: () => void }> = ({ label, value, color, onClick }) => (
  <Card variant="outlined" sx={{ height: '100%', cursor: onClick ? 'pointer' : 'default' }} onClick={onClick}>
    <CardContent sx={{ py: 1.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</Typography>
      <Typography variant="h6" sx={{ fontWeight: 700, color }}>{value}</Typography>
    </CardContent>
  </Card>
);

const Supplier360Page: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [d, setD] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { financeWorkspaceService.supplier360(Number(id)).then(setD).catch((e) => setError(e.message)).finally(() => setLoading(false)); }, [id]);
  if (loading) return <Box sx={{ p: 3 }}><LinearProgress /></Box>;
  if (error || !d) return <Box sx={{ p: 3 }}><Alert severity="error">{error || 'Not found'}</Alert></Box>;
  const s = d.summary || {};
  const journey: JourneyStage[] = [
    { label: 'PO', state: s.pos > 0 ? 'done' : 'pending' },
    { label: 'Billed', state: s.bills > 0 ? 'done' : 'pending' },
    { label: 'TDS', state: s.tds > 0 ? 'done' : 'pending' },
    { label: 'Payable', state: s.payable > 0 ? 'active' : (s.bills > 0 ? 'done' : 'pending') },
    { label: 'Paid', state: s.paid > 0 && s.payable <= 0 ? 'done' : (s.paid > 0 ? 'active' : 'pending') },
  ];
  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <IconButton onClick={() => navigate(-1)}><ArrowBack /></IconButton>
        <Avatar sx={{ bgcolor: 'secondary.main' }}><LocalShipping /></Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>{d.profile.name}</Typography>
          <Typography variant="body2" color="text.secondary">
            {d.profile.gstin ? <>GSTIN <b style={{ fontFamily: 'monospace' }}>{d.profile.gstin}</b> · </> : ''}{d.profile.pan ? <>PAN <b style={{ fontFamily: 'monospace' }}>{d.profile.pan}</b></> : ''}
          </Typography>
        </Box>
        <Button variant="outlined" onClick={() => navigate('/procurement/payments')}>Payments</Button>
      </Stack>

      <Card variant="outlined" sx={{ mb: 2 }}><CardContent sx={{ py: 1.5 }}>
        <Typography variant="overline" color="text.secondary">Procure-to-Pay Lifecycle</Typography>
        <BusinessJourney stages={journey} />
      </CardContent></Card>

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid size={{ xs: 6, md: 2.4 }}><Stat label="Purchase Orders" value={s.pos ?? 0} /></Grid>
        <Grid size={{ xs: 6, md: 2.4 }}><Stat label="Billed" value={formatIndianNumber(s.billed || 0)} /></Grid>
        <Grid size={{ xs: 6, md: 2.4 }}><Stat label="Payable" value={formatIndianNumber(s.payable || 0)} color="warning.main" /></Grid>
        <Grid size={{ xs: 6, md: 2.4 }}><Stat label="Paid" value={formatIndianNumber(s.paid || 0)} color="success.main" /></Grid>
        <Grid size={{ xs: 6, md: 2.4 }}><Stat label="TDS Deducted" value={formatIndianNumber(s.tds || 0)} onClick={() => navigate('/finance/tds-reports')} /></Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Bills</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead><TableRow sx={{ '& th': { fontWeight: 700 } }}><TableCell>Invoice</TableCell><TableCell>Date</TableCell><TableCell align="right">Amount</TableCell><TableCell>Status</TableCell></TableRow></TableHead>
              <TableBody>
                {d.invoices.map((i: any, idx: number) => (
                  <TableRow key={idx} hover><TableCell sx={{ fontFamily: 'monospace' }}>{i.ref}</TableCell><TableCell>{String(i.date).slice(0, 10)}</TableCell><TableCell align="right">{formatIndianNumber(i.amount)}</TableCell><TableCell><Chip size="small" label={i.status} /></TableCell></TableRow>
                ))}
                {!d.invoices.length && <TableRow><TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }}>No bills.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, mt: 2, display: 'flex', alignItems: 'center', gap: 0.5 }}><Payments fontSize="small" /> Payments</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead><TableRow sx={{ '& th': { fontWeight: 700 } }}><TableCell>Payment</TableCell><TableCell>Date</TableCell><TableCell align="right">Gross</TableCell><TableCell align="right">TDS</TableCell><TableCell align="right">Net</TableCell></TableRow></TableHead>
              <TableBody>
                {d.payments.map((p: any, idx: number) => (
                  <TableRow key={idx} hover><TableCell sx={{ fontFamily: 'monospace' }}>{p.ref}</TableCell><TableCell>{String(p.date).slice(0, 10)}</TableCell><TableCell align="right">{formatIndianNumber(p.gross)}</TableCell><TableCell align="right">{formatIndianNumber(p.tds)}</TableCell><TableCell align="right">{formatIndianNumber(p.net)}</TableCell></TableRow>
                ))}
                {!d.payments.length && <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>No payments.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
        <Grid size={{ xs: 12, md: 5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Activity Timeline</Typography>
          <Card variant="outlined"><CardContent>
            <Box sx={{ position: 'relative', pl: 2, '&:before': { content: '""', position: 'absolute', left: 5, top: 4, bottom: 4, width: 2, bgcolor: 'divider' } }}>
              {d.timeline.map((t: any, i: number) => (
                <Box key={i} sx={{ position: 'relative', pb: 1.5 }}>
                  <Box sx={{ position: 'absolute', left: -15, top: 4, width: 10, height: 10, borderRadius: '50%', bgcolor: t.kind === 'payment' ? 'warning.main' : 'primary.main' }} />
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{String(t.ts).slice(0, 10)}</Typography>
                  <Typography variant="body2">{t.label}</Typography>
                </Box>
              ))}
              {!d.timeline.length && <Typography variant="body2" color="text.secondary">No activity.</Typography>}
            </Box>
          </CardContent></Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Supplier360Page;
